import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Card, FormControl, Grid, Stack } from '@mui/material';

// routes
import { PATH_DASHBOARD } from '../../../../routes/paths';

// components
import Iconify from '../../../../components/Iconify';
import { FormProvider, RHFTextField, RHFSelect } from '../../../../components/hook-form';
import { useStore } from 'src/stores/store';
import { observer } from 'mobx-react-lite';
import useLocales from 'src/hooks/useLocales';
import { Province } from 'src/@types/province';
// ----------------------------------------------------------------------

export default observer(function ProvinceNewEditForm() {
  const { ProvinceStore, commonDropdown } = useStore();
  const { translate } = useLocales();
  const { createProvince, updateProvince, editMode, selectedProvince, clearSelectedProvince } =
    ProvinceStore;

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewProvinceSchema = Yup.object().shape({
    englishName: Yup.string().required(`${translate('Validation.EnglishName')}`),
    dariName: Yup.string().required(`${translate('Validation.DariName')}`),
    pashtoName: Yup.string().required(`${translate('Validation.PashtoName')}`),
    code: Yup.string().required(`${translate('Validation.Code')}`),
  });

  const defaultValues = useMemo<Province>(
    () => ({
      id: selectedProvince?.id,
      englishName: selectedProvince?.englishName || '',
      dariName: selectedProvince?.dariName || '',
      pashtoName: selectedProvince?.pashtoName || '',

      code: selectedProvince?.code || '',
    }),
    [selectedProvince]
  );

  const methods = useForm<Province>({
    resolver: yupResolver(NewProvinceSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: Province) => {
    if (data.id! === undefined) {
      ///create
      await createProvince(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
          navigate(PATH_DASHBOARD.Province.list);
          clearSelectedProvince();
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error.EnglishName != null) {
            setError('afterSubmit', { ...err, message: json.error.EnglishName });
          } else if (json.error.DariName != null) {
            setError('afterSubmit', { ...err, message: json.error.DariName });
          } else if (json.error.PashtoName != null) {
            setError('afterSubmit', { ...err, message: json.error.PashtoName });
          }
        });
    } else {
      ///update
      await updateProvince(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
          navigate(PATH_DASHBOARD.Province.list);
          clearSelectedProvince();
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error.EnglishName != null) {
            setError('afterSubmit', { ...err, message: json.error.EnglishName });
          } else if (json.error.DariName != null) {
            setError('afterSubmit', { ...err, message: json.error.DariName });
          } else if (json.errors.PashtoName != null) {
            setError('afterSubmit', { ...err, message: json.error.PashtoName });
          }
        });
    }
  };

  useEffect(() => {
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, editMode, defaultValues]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              {/* <Autocomplete
                id="parentId"
                getOptionLabel={(loadDepartmentDropdown) => `${loadDepartmentDropdown.englishName}`} 
                options={loadDepartmentDropdown}
               
              /> */}
              <RHFTextField
                name="englishName"
                label={translate('GeneralFields.EnglishName')}
                showAsterisk={true}
                autoFocus
              />
              <RHFTextField
                name="pashtoName"
                label={translate('GeneralFields.PashtoName')}
                showAsterisk={true}
              />
              <RHFTextField
                name="dariName"
                label={translate('GeneralFields.DariName')}
                showAsterisk={true}
              />

              <RHFTextField
                name="code"
                label={translate('GeneralFields.Code')}
                showAsterisk={true}
              />
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedProvince();
                  navigate(PATH_DASHBOARD.Province.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
